import { useContext } from "react";
import { useLocation } from "react-router-dom";

import {
	GetUserCompanyConfigurationToggle,
	UserCompanyConfigurationKeys,
} from "@/model/UserCompanyConfiguration/UserCompanyConfiguration";
import { UserCompanyConfigurationContext } from "@/model/UserCompanyConfiguration/UserCompanyConfigurationContext";

import {
	useFetchActiveProcedureDetails,
	useHandleProcedureDetails,
} from "@/service/ProcedureQueries";

export const useQueryParam = (query: string): string | undefined => {
	const search = useLocation().search;

	if (query) {
		const result = new URLSearchParams(search).get(query);

		if (result) {
			return result;
		}
	}

	return undefined;
};

export const useQueryParamHide = (options?: {
	overrideForSpecificPages: boolean;
}) => {
	const queryHide = useQueryParam("hide");
	const page = window?.location?.hash;

	if (options?.overrideForSpecificPages && page === "#/dashboard") {
		return true;
	}

	if (queryHide) {
		return Boolean(parseInt(queryHide));
	}

	return false;
};

export const useClassicMode = (
	activeSchemeId: number | undefined,
	procedureId?: any,
) => {
	const classicModeUrlParameter = useQueryParam("classic");

	const userCompanyConfiguration = useContext(UserCompanyConfigurationContext);
	const { data: ProcedureDetails } =
		useFetchActiveProcedureDetails(procedureId);

	const { activeChildNode } = useHandleProcedureDetails(
		ProcedureDetails?.ProcedureDetailNodes,
		activeSchemeId,
	);

	const isImportedChecklist = activeChildNode?.ImportedChecklist;
	const isNotDefaultChecklistDesign =
		ProcedureDetails && ProcedureDetails.ViewDesign !== "Default";

	if (!isImportedChecklist) {
		// If the checklist is manual, use SATOS5 checklist design
		return false;
	} else if (classicModeUrlParameter) {
		// If classic mode is specified in URL parameters use that
		return Boolean(parseInt(classicModeUrlParameter));
	} else if (isNotDefaultChecklistDesign) {
		const procedureChecklistDesignSetting =
			ProcedureDetails.ViewDesign === "Classic";

		return procedureChecklistDesignSetting;
	}

	// If procedure does not have a checklist design assigned, use the default design from organization settings
	const defaultChecklistDesign = GetUserCompanyConfigurationToggle(
		UserCompanyConfigurationKeys.ClassicMode,
		userCompanyConfiguration,
	);

	return defaultChecklistDesign;
};

export const useHideShift = () => {
	const userCompanyConfiguration = useContext(UserCompanyConfigurationContext);

	return Boolean(
		Number(
			userCompanyConfiguration?.Configuration[
				UserCompanyConfigurationKeys.HideShift
			],
		),
	);
};
